import { create } from "zustand"
import type { User, Session } from "@supabase/supabase-js"
import { getBrowserSupabaseClient } from "@/lib/supabase/client"
import {
  signInWithEmail as authSignInWithEmail,
  signInWithPhone as authSignInWithPhone,
  signUpWithEmail as authSignUpWithEmail,
  signOut as authSignOut,
  onAuthStateChange,
} from "@/lib/supabase/auth"

interface AuthState {
  user: User | null
  session: Session | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  initialize: () => () => void
  signInWithEmail: (email: string, password: string) => Promise<void>
  signInWithPhone: (phone: string, otp?: string) => Promise<{ requiresOtp: boolean }>
  signUpWithEmail: (email: string, password: string, nickname?: string, inviteCode?: string, school?: string) => Promise<void>
  logout: () => Promise<void>
  clearError: () => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  initialize: () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) {
      set({ isLoading: false })
      return () => {}
    }

    // 获取初始会话
    supabase.auth.getSession().then(({ data: { session } }) => {
      set({
        session,
        user: session?.user ?? null,
        isAuthenticated: !!session,
        isLoading: false,
      })
    })

    // 监听认证状态变化
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      set({
        session,
        user: session?.user ?? null,
        isAuthenticated: !!session,
        isLoading: false,
      })
    })

    return () => {
      data.subscription.unsubscribe()
    }
  },

  signInWithEmail: async (email, password) => {
    set({ isLoading: true, error: null })
    const supabase = getBrowserSupabaseClient()
    if (!supabase) {
      set({ error: "Supabase 未配置", isLoading: false })
      return
    }

    const { error } = await authSignInWithEmail(supabase, { email, password })
    if (error) {
      set({ error, isLoading: false })
      throw new Error(error)
    }
    // onAuthStateChange 会自动更新 state
  },

  signInWithPhone: async (phone, otp) => {
    set({ isLoading: true, error: null })
    const supabase = getBrowserSupabaseClient()
    if (!supabase) {
      set({ error: "Supabase 未配置", isLoading: false })
      return { requiresOtp: false }
    }

    const result = await authSignInWithPhone(supabase, { phone, otp })
    if (result.error) {
      set({ error: result.error, isLoading: false })
      return { requiresOtp: false }
    }

    if (result.requiresOtp) {
      set({ isLoading: false })
      return { requiresOtp: true }
    }

    return { requiresOtp: false }
  },

  signUpWithEmail: async (email, password, nickname, inviteCode, school) => {
    set({ isLoading: true, error: null })
    const supabase = getBrowserSupabaseClient()
    if (!supabase) {
      set({ error: "Supabase 未配置", isLoading: false })
      return
    }

    const { error } = await authSignUpWithEmail(supabase, { email, password, nickname, inviteCode, school })
    if (error) {
      set({ error, isLoading: false })
      return
    }
  },

  logout: async () => {
    const supabase = getBrowserSupabaseClient()
    if (!supabase) {
      set({ user: null, session: null, isAuthenticated: false })
      return
    }

    const { error } = await authSignOut(supabase)
    if (error) {
      set({ error })
      return
    }
    // onAuthStateChange 会自动清除 state
  },

  clearError: () => {
    set({ error: null })
  },
}))
